import * as React from 'react';

interface Props {
  /** Visual size scale. md = default for navs, sm = compact, lg = auth-page hero. */
  size?: 'sm' | 'md' | 'lg';
  /** Show only the coral-dot mark, no wordmark (collapsed sidebar). */
  markOnly?: boolean;
  /**
   * 'auto' (default) swaps the black/white wordmark with the theme.
   * 'on-dark' always renders the white wordmark — for surfaces that stay dark
   * in BOTH themes (e.g. the marketing footer).
   */
  variant?: 'auto' | 'on-dark';
  className?: string;
}

const SIZES = {
  sm: { word: 'h-5', mark: 'h-6 w-6' },
  md: { word: 'h-6', mark: 'h-7 w-7' },
  lg: { word: 'h-9', mark: 'h-10 w-10' },
} as const;

/**
 * Official Worqly brand mark. Renders the supplied wordmark PNGs, swapping the
 * black (light-bg) and white (dark-bg) variants automatically with the theme.
 * `markOnly` shows just the coral dot for the collapsed sidebar.
 * Assets generated by frontend/scripts/gen-brand-assets.js.
 */
export function WorqlyMark({ size = 'md', markOnly = false, variant = 'auto', className = '' }: Props) {
  const s = SIZES[size];

  if (markOnly) {
    return (
      <img
        src="/brand/worqly-mark.png"
        alt="Worqly"
        className={[s.mark, 'object-contain select-none', className].join(' ')}
        draggable={false}
      />
    );
  }

  if (variant === 'on-dark') {
    return (
      <span className={['inline-flex items-center', className].join(' ')} aria-label="Worqly">
        <img
          src="/brand/worqly-wordmark-dark.png"
          alt="Worqly"
          className={[s.word, 'w-auto object-contain select-none block'].join(' ')}
          draggable={false}
        />
      </span>
    );
  }

  return (
    <span className={['inline-flex items-center', className].join(' ')} aria-label="Worqly">
      <img
        src="/brand/worqly-wordmark-light.png"
        alt="Worqly"
        className={[s.word, 'w-auto object-contain select-none block dark:hidden'].join(' ')}
        draggable={false}
      />
      <img
        src="/brand/worqly-wordmark-dark.png"
        alt="Worqly"
        className={[s.word, 'w-auto object-contain select-none hidden dark:block'].join(' ')}
        draggable={false}
        aria-hidden="true"
      />
    </span>
  );
}
